import Tabs from "components/BaseWidgets/tab";
import { ExploreContext } from "context/ExploreContext";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import "./style.scss";
import Segmented from "components/BaseWidgets/Segmented";
import { GridIcon, ListIcon } from "assets/icons";
import ExploreHeader from "components/Explore/components/ExploreHeader";
import { CardLists } from "../CardLists";
import { useLocation } from "react-router-dom";
import {
  useProfileInterestedCount,
  useImageRequestCount,
} from "hooks/api/useEngage";

function EngageLayout({
  tabVariant = "1",
  data = [],
  setProfileType,
  setVisitedPage,
  count,
  current,
  setCurrent,
  tabActive,
}) {
  const { cardLayout, setCardLayout, clearAll, setIsNotInterested } =
    useContext(ExploreContext);
  const location = useLocation();
  const initialTabId = useMemo(() => location.state?.activeTabId || "1", []);
  const [activeTab, setActiveTab] = useState(initialTabId);
  const [secondaryTabActive, setSecondaryTabActive] = useState("1");

  // Restore sub-tabs from sessionStorage if returning from profile
  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;
    const savedSubTab = sessionStorage.getItem("engageSubTab");
    const savedSecondaryTab = sessionStorage.getItem("engageSecondaryTab");

    if (isFromProfileDetail) {
      if (savedSubTab) {
        setActiveTab(savedSubTab);
      }
      if (savedSecondaryTab) {
        setSecondaryTabActive(savedSecondaryTab);
        setProfileType?.(Number(savedSecondaryTab));
      }
    }
  }, []);
  const [imageCount, setImageCount] = useState({});
  const [profileInterestCount, setProfileInterestCount] = useState({});

  const shouldFetchInterestedCount = useMemo(() => {
    return (
      tabActive === "2" && tabVariant === "2" && ["1", "2"].includes(activeTab)
    );
  }, [tabVariant, activeTab]);

  const shouldFetchImageRequestCount = useMemo(() => {
    return (
      tabActive === "4" && tabVariant === "4" && ["1", "2"].includes(activeTab)
    );
  }, [tabVariant, activeTab]);

  const interestedProfileType = useMemo(() => {
    if (tabVariant === "2") {
      if (activeTab === "1") return 1;
      if (activeTab === "2") return 2;
    }
    return undefined; // will disable hook
  }, [tabVariant, activeTab]);

  const imageRequestCountType = useMemo(() => {
    if (tabVariant === "4") {
      if (activeTab === "1") return 1;
      if (activeTab === "2") return 2;
    }
    return undefined; // will disable hook
  }, [tabVariant, activeTab]);

  const { data: interestedCount, isLoading: isInterestedCountLoading } =
    useProfileInterestedCount(
      {
        profile_type: interestedProfileType,
      },
      {
        enabled: shouldFetchInterestedCount,
      },
    );

  const { data: imageRequestCount, isLoading: isImageRequestCountLoading } =
    useImageRequestCount(
      {
        profile_type: imageRequestCountType,
      },
      {
        enabled: shouldFetchImageRequestCount,
      },
    );

  useEffect(() => {
    if (imageRequestCount?.image_request_count) {
      setImageCount(imageRequestCount?.image_request_count);
    }
    if (interestedCount?.interested_count) {
      setProfileInterestCount(interestedCount?.interested_count);
    }
  }, [imageRequestCount, interestedCount]);

  const tabLabels = {
    1: ["Visited by me", "Who visited mine"],
    2: [
      "Profiles I’m interested in",
      "Who’s interested in me?",
      "Not Interested by me",
    ],
    3: ["Favorited by me", "Who favorited me?"],
    4: ["Requests by me", "Requests received"],
    5: ["Contact viewed by me", "Who viewed my contact?"],
  };
  const secondaryTabs = (tabVariant, labelIndex) => {
    if (tabVariant === "2") {
      if (labelIndex === 1)
        return [
          {
            key: "5",
            label: `Received${
              profileInterestCount?.pending_count > 0
                ? ` (${profileInterestCount?.pending_count})`
                : ""
            }`,
          },
          {
            key: "6",
            label: `Accepted${
              profileInterestCount?.accepted_count > 0
                ? ` (${profileInterestCount?.accepted_count})`
                : ""
            }`,
          },
          {
            key: "7",
            label: `Rejected${
              profileInterestCount?.rejected_count > 0
                ? ` (${profileInterestCount?.rejected_count})`
                : ""
            }`,
          },
        ];
      return [
        {
          key: "1",
          label: `Sent${
            profileInterestCount?.pending_count > 0
              ? ` (${profileInterestCount?.pending_count})`
              : ""
          }`,
        },
        {
          key: "2",
          label: `Accepted${
            profileInterestCount?.accepted_count > 0
              ? ` (${profileInterestCount?.accepted_count})`
              : ""
          }`,
        },
        {
          key: "3",
          label: `Rejected${
            profileInterestCount?.rejected_count > 0
              ? ` (${profileInterestCount?.rejected_count})`
              : ""
          }`,
        },
        {
          key: "4",
          label: `Cancelled${
            profileInterestCount?.cancelled_count > 0
              ? ` (${profileInterestCount?.cancelled_count})`
              : ""
          }`,
        },
      ];
    }
    if (tabVariant === "4") {
      if (labelIndex === 1)
        return [
          {
            key: "5",
            label: `Received${
              imageCount?.pending_count > 0
                ? ` (${imageCount?.pending_count})`
                : ""
            }`,
          },
          {
            key: "6",
            label: `Accepted${
              imageCount?.accepted_count > 0
                ? ` (${imageCount?.accepted_count})`
                : ""
            }`,
          },
          {
            key: "7",
            label: `Rejected${
              imageCount?.rejected_count > 0
                ? ` (${imageCount?.rejected_count})`
                : ""
            }`,
          },
        ];
      return [
        {
          key: "1",
          label: `Sent${
            imageCount?.pending_count > 0
              ? ` (${imageCount?.pending_count})`
              : ""
          }`,
        },
        {
          key: "2",
          label: `Accepted${
            imageCount?.accepted_count > 0
              ? ` (${imageCount?.accepted_count})`
              : ""
          }`,
        },
        {
          key: "3",
          label: `Rejected${
            imageCount?.rejected_count > 0
              ? ` (${imageCount?.rejected_count})`
              : ""
          }`,
        },
        {
          key: "4",
          label: `Cancelled${
            imageCount?.cancelled_count > 0
              ? ` (${imageCount?.cancelled_count})`
              : ""
          }`,
        },
      ];
    }
    return [];
  };
  const profileTypeMap = useMemo(
    () => ({
      1: { 1: 1, 2: 2 },
      2: { 1: 1, 2: 5, 3: 9 },
      3: { 1: 1, 2: 2 },
      4: { 1: 1, 2: 5 },
      5: { 1: 1, 2: 2 },
    }),
    [],
  );
  const restoredRef = useRef(false);

  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;

    if (isFromProfileDetail && !restoredRef.current) {
      const savedSubTab = sessionStorage.getItem("engageSubTab");
      const savedSecondaryTab = sessionStorage.getItem("engageSecondaryTab");

      if (savedSubTab) {
        setActiveTab(savedSubTab);
      }
      if (savedSecondaryTab) {
        setSecondaryTabActive(savedSecondaryTab);
        setProfileType?.(Number(savedSecondaryTab));
      }
      restoredRef.current = true;
      return;
    }

    // Default reset when tabVariant changes (unless restoring)
    setActiveTab("1");
    setSecondaryTabActive("1");
    setIsNotInterested(false);
    if (tabVariant && setProfileType) {
      const mapping = profileTypeMap[tabVariant];
      if (mapping?.["1"]) {
        setProfileType(mapping["1"]);
      }
    }
    restoredRef.current = true;
  }, [tabVariant, setProfileType]);

  useEffect(() => {
    const activeTabId = location?.state?.activeTabId;
    if (activeTabId) {
      setActiveTab(activeTabId);

      // Mirror the same logic as the main tab onChange handler so that
      // secondary tabs and profile_type are in sync when navigating from notifications.
      const SetToReceived =
        ["2", "4"].includes(tabVariant) && activeTabId === "2";
      const newSecondary = SetToReceived ? "5" : "1";

      setSecondaryTabActive(newSecondary);
      if (setProfileType) {
        setProfileType(Number(newSecondary));
      }

      if (tabVariant && setProfileType) {
        const mapping = profileTypeMap[tabVariant];
        if (mapping?.[activeTabId]) {
          setProfileType(mapping[activeTabId]);
        }
      }
    }
  }, [location?.state?.activeTabId, tabVariant, setProfileType]);

  const labels = tabLabels[tabVariant];

  const childTabItems = labels.map((label, index) => ({
    key: `${index + 1}`,
    label,
    children: (
      <div className="flex flex-col gap-6 mt-6">
        {((tabVariant === "2" && index !== 2) || tabVariant === "4") && (
          <Tabs
            activeKey={secondaryTabActive}
            onChange={(key) => {
              setSecondaryTabActive(key);
              setVisitedPage(1);
              if (setProfileType) {
                setProfileType(Number(key));
              }
            }}
            variant="secondary"
            className="tabs-secondary"
            items={secondaryTabs(tabVariant, index)}
          />
        )}
        <ExploreHeader
          segment={false}
          isEngage
          isContact={tabVariant === "5" || (tabVariant === "2" && index == 2)}
          pending={0}
          totalProfiles={count?.count || 0}
        />

        <CardLists
          data={data}
          count={count}
          current={current}
          setCurrent={setCurrent}
          activeTab={activeTab}
          tabVariant={tabVariant}
          label={label}
          secondaryTabActive={secondaryTabActive}
        />
      </div>
    ),
  }));

  useEffect(() => {
    if (activeTab == "3") {
      setIsNotInterested(true);
    } else {
      setIsNotInterested(false);
    }
  }, [activeTab]);

  return (
    <div className="flex flex-col gap-6 pl-10 pt-3 explore-layout engage-layout">
      <Tabs
        activeKey={activeTab}
        className="engage-child-tabs"
        onChange={(key) => {
          clearAll();
          setActiveTab(key);
          setVisitedPage(1);
          const SetToReceived = ["2", "4"].includes(tabVariant) && key === "2";
          const newSecondary = SetToReceived ? "5" : "1";
          setSecondaryTabActive(newSecondary);
          setProfileType?.(Number(newSecondary));
          const mapping = profileTypeMap[tabVariant];
          if (mapping?.[key]) {
            setProfileType(mapping[key]);
          }
        }}
        variant="primary"
        items={childTabItems}
        extraContent={{
          right: (
            <Segmented
              state={cardLayout}
              setState={setCardLayout}
              options={[
                { value: "grid", icon: <GridIcon /> },
                { value: "list", icon: <ListIcon /> },
              ]}
            />
          ),
        }}
      />
    </div>
  );
}

export default EngageLayout;
